import React, { useEffect, useState } from 'react';
import clsx from 'clsx';
import { Link } from 'react-router-dom';
import type { PostMatchInsight } from '../../../shared/whatsapp/insights';
import { usePostMatchAnalysis } from '../../../hooks/usePostMatchAnalysis';
import { SheetLabel, UtSheet } from './UtSheet';
import { groupReportInsights } from './reportGroups';
import { shortDate } from './dates';

interface ReportProps {
  open: boolean;
  onClose: () => void;
  matchNumber: number;
  date: string;
  scoreBlue: number | null;
  scoreOrange: number | null;
  insights: PostMatchInsight[];
  /** Built by buildPostMatchMessage (src/shared/whatsapp), the same text the admin copy button and the bot use. */
  whatsappSummary: string;
  loading?: boolean;
  /** Show a link to /results/:seq (when opened away from the match page). */
  linkToMatch?: boolean;
}

/** Post-match report sheet: score, insights grouped by kind, and the WhatsApp summary with Copy. */
export const UtPostMatchReport: React.FC<ReportProps> = ({
  open,
  onClose,
  matchNumber,
  date,
  scoreBlue,
  scoreOrange,
  insights,
  whatsappSummary,
  loading,
  linkToMatch,
}) => {
  const [copied, setCopied] = useState(false);
  const groups = groupReportInsights(insights);

  useEffect(() => {
    if (open) setCopied(false);
  }, [open]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(whatsappSummary);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  return (
    <UtSheet open={open} onClose={onClose} eyebrow={`Match #${matchNumber} · ${shortDate(date)}`} title="Post-match report" cancelLabel="Close">
      <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-2.5 rounded-xl bg-ut-card p-3.5">
        <div className="flex flex-col items-center gap-0.5">
          <span className="font-ut-display text-sm font-extrabold tracking-[0.08em] text-ut-blue">BLUE</span>
          <span className="font-ut-display text-5xl font-extrabold leading-none">{scoreBlue ?? '–'}</span>
        </div>
        <span className="font-ut-display text-[22px] font-extrabold text-ut-mut">–</span>
        <div className="flex flex-col items-center gap-0.5">
          <span className="font-ut-display text-sm font-extrabold tracking-[0.08em] text-ut-orange">ORANGE</span>
          <span className="font-ut-display text-5xl font-extrabold leading-none">{scoreOrange ?? '–'}</span>
        </div>
      </div>

      {loading && groups.length === 0 && <span className="text-sm text-ut-mut">Loading the report…</span>}
      {!loading && groups.length === 0 && <span className="text-sm text-ut-mut">No insights for this match yet.</span>}

      {groups.map((g) => (
        <div key={g.key} className="flex flex-col gap-1.5">
          <SheetLabel>{g.label}</SheetLabel>
          {g.insights.map((i) => (
            <div
              key={i.id}
              className={clsx(
                'flex items-baseline gap-2.5 rounded-[10px] border px-3 py-2.5 text-sm leading-[1.4]',
                g.key === 'headlines' ? 'border-ut-acc bg-ut-acc-soft' : 'border-ut-line bg-ut-card',
              )}
            >
              <span
                className={clsx(
                  'h-1.5 w-1.5 flex-none -translate-y-0.5 rounded-full',
                  g.key === 'headlines' ? 'bg-ut-acc' : i.priority <= 3 ? 'bg-ut-sky' : 'bg-ut-mut',
                )}
              />
              <span>{i.headline}</span>
            </div>
          ))}
        </div>
      ))}

      {whatsappSummary && (
        <div className="flex flex-col gap-2 rounded-xl bg-ut-card p-3">
          <div className="flex items-center justify-between">
            <SheetLabel>WhatsApp summary</SheetLabel>
            <button type="button" onClick={copy} className="rounded-[7px] border border-ut-line px-2.5 py-1.5 text-xs font-bold hover:bg-ut-card2">
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
          <pre className="m-0 whitespace-pre-wrap rounded-lg bg-ut-field p-2.5 font-mono text-xs leading-normal text-ut-ink">{whatsappSummary}</pre>
        </div>
      )}

      {linkToMatch && (
        <Link to={`/results/${matchNumber}`} onClick={onClose} className="self-start text-[13px] font-semibold text-ut-acc hover:text-ut-ink">
          Full match page →
        </Link>
      )}
    </UtSheet>
  );
};

interface LoaderProps {
  open: boolean;
  onClose: () => void;
  game: { id: string; sequence_number: number; date: string; score_blue: number | null; score_orange: number | null };
}

/** The report for a game the page hasn't loaded insights for; fetches them (usePostMatchAnalysis) only while open. */
export const UtPostMatchReportLoader: React.FC<LoaderProps> = ({ open, onClose, game }) => {
  const { insights, whatsappSummary, loading } = usePostMatchAnalysis(open ? game.id : null);
  return (
    <UtPostMatchReport
      open={open}
      onClose={onClose}
      matchNumber={game.sequence_number}
      date={game.date}
      scoreBlue={game.score_blue}
      scoreOrange={game.score_orange}
      insights={insights}
      whatsappSummary={whatsappSummary}
      loading={loading}
      linkToMatch
    />
  );
};
